// After a deploy the previous build's code files are gone, so a tab left open fails to load its next
// lazily loaded file (Vite reports it as `vite:preloadError`). Reloading once picks up the new build. A
// second failure soon after that reload is left to surface instead, so a file that is really missing
// never starts a reload loop.

const STORAGE_KEY = 'lureo.frontend.staleBuildReloadAt';

/** How long after a reload for a stale build another failure is left to surface rather than reloading again. */
export const RELOAD_GUARD_MS = 60_000;

export interface StaleBuildReloadDeps {
  /** The tab's session storage, read and written in a try: it can throw where storage is blocked. */
  storage: () => Pick<Storage, 'getItem' | 'setItem'>;
  now: () => number;
  reload: () => void;
}

/** Reloads, unless this tab already reloaded for a stale build within RELOAD_GUARD_MS. True when it reloads. */
export function reloadForStaleBuild(deps: StaleBuildReloadDeps): boolean {
  try {
    const storage = deps.storage();
    const last = Number(storage.getItem(STORAGE_KEY));
    if (Number.isFinite(last) && deps.now() - last < RELOAD_GUARD_MS) return false;
    storage.setItem(STORAGE_KEY, String(deps.now()));
  } catch {
    // Without storage the reloaded page could not tell it had reloaded already: no reload, no loop
    return false;
  }
  deps.reload();
  return true;
}

/** Reloads the page once when a code file of an older build fails to load. */
export function installStaleBuildReload(target: Window = window): void {
  target.addEventListener('vite:preloadError', event => {
    const reloading = reloadForStaleBuild({
      storage: () => target.sessionStorage,
      now: () => Date.now(),
      reload: () => target.location.reload(),
    });
    if (!reloading) return;
    console.warn('[deploy] a code file of an older build failed to load; reloading for the new build');
    // Reloading answers it, so the failed import is not reported as an error
    event.preventDefault();
  });
}
